import React, { useContext, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { HospitalContext } from '../../context/HospitalContext'
import ExportButton from '../Common/ExportButton'
import './Sidebar.css'

interface SidebarLink {
    label: string
    path?: string
    submenu?: SidebarLink[]
}

interface SidebarProps {
    logo: string
    links: SidebarLink[]
    exportPages?: { url: string; title: string }[]
    exportTitle?: string
    onDownload?: () => void
}

const Sidebar: React.FC<SidebarProps> = ({ logo, links, exportPages, exportTitle, onDownload }) => {
    const location = useLocation()
    const { triggerRefresh } = useContext(HospitalContext)

    const handleLinkClick = (path: string) => {
        if (location.pathname === path) {
            triggerRefresh()
        }
    }

    const [openSubmenus, setOpenSubmenus] = React.useState<Record<string, boolean>>(() => {
        const initial: Record<string, boolean> = {}
        links.forEach(link => {
            if (link.submenu && link.submenu.some(sub => sub.path && location.pathname === sub.path)) {
                initial[link.label] = true
            }
        })
        return initial
    })

    useEffect(() => {
        const autoOpen: Record<string, boolean> = {}
        for (const link of links) {
            if (link.submenu) {
                const hasActiveChild = link.submenu.some(sub => sub.path && location.pathname === sub.path)
                if (hasActiveChild) {
                    autoOpen[link.label] = true
                }
            }
        }
        if (Object.keys(autoOpen).length > 0) {
            setOpenSubmenus(prev => ({ ...prev, ...autoOpen }))
        }
    }, [location.pathname, links])

    const toggleSubmenu = (label: string) => {
        setOpenSubmenus(prev => ({ ...prev, [label]: !prev[label] }))
    }

    const renderLink = (link: SidebarLink) => {
        if (link.submenu) {
            const isOpen = openSubmenus[link.label]
            const isChildActive = link.submenu.some(sub => location.pathname === sub.path)

            return (
                <div key={link.label}>
                    <div
                        className={`toggle-submenu ${isOpen ? 'open' : ''} ${isChildActive ? 'active-parent' : ''}`}
                        onClick={() => toggleSubmenu(link.label)}
                    >
                        {link.label}
                    </div>
                    {isOpen && (
                        <div className="submenu">
                            {link.submenu.map(sub => (
                                <NavLink
                                    key={sub.path}
                                    to={`${sub.path || ''}${location.search}`}
                                    className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}
                                    onClick={() => sub.path && handleLinkClick(sub.path)}
                                >
                                    {sub.label}
                                </NavLink>
                            ))}
                        </div>
                    )}
                </div>
            )
        }

        return (
            <NavLink
                key={link.path}
                to={`${link.path || ''}${location.search}`}
                className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}
                onClick={() => link.path && handleLinkClick(link.path)}
            >
                {link.label}
            </NavLink>
        )
    }

    return (
        <nav className="sidebar">
            <div className="logo">
                <img src={logo} alt="ILPQC Logo" />
            </div>

            <div className="nav-links">
                {links.map(renderLink)}
            </div>

            <div className="sidebar-spacer"></div>

            {exportPages && (
                <ExportButton pages={exportPages} title={exportTitle} className="download-tab" />
            )}

            {!exportPages && onDownload && (
                <button onClick={onDownload} className="tab download-tab">
                    <span className="label">Download</span>
                </button>
            )}
        </nav>
    )
}

export default Sidebar
